/**
 * Sale Banner Custom Element
 *
 * Uses Shopify's Section Rendering API to re-fetch server-rendered content
 * when utility partner selection changes. This keeps all display logic in Liquid.
 *
 * Data Attributes (from Liquid):
 * @attr {string} data-section-id - Fallback section ID if parent wrapper not found
 * @attr {string} data-block-id - Block ID to locate this specific banner in re-rendered HTML
 *
 * Listened Events:
 * @event mysa:utility-partner-changed - Re-renders when utility is selected via UI
 * @event mysa:utility-partner-cleared - Re-renders when utility selection is cleared
 *
 * Note: We do NOT listen to mysa:utility-partner-loaded because server-side rendering
 * handles the initial state from cart.attributes._utility_partner_code
 *
 * Behavior:
 * - On any utility event, fetches fresh section HTML from server
 * - Dynamically finds parent section ID from shopify-section-* wrapper
 * - Server re-renders with current cart.attributes._utility_partner_code
 * - Parses response and replaces own className and innerHTML
 * - Debounces rapid events to prevent multiple concurrent requests
 *
 * Fallback Behavior:
 * - If Section Rendering API fails (404, network error, etc.), falls back to manual JS update
 * - Reads utility provider data from [data-utility-providers] JSON script tag
 * - Updates banner class, text, logo, and icon visibility directly via DOM manipulation
 * - Ensures banner still updates even on pages where the section doesn't exist
 */

import { Component } from "@theme/component";
import { createLogger, createReporter } from "@theme/logger";
import { MysaEvents } from "@theme/mysa-events";

const log = createLogger("SaleBanner");
const reportError = createReporter("SaleBanner");

class SaleBanner extends Component {
  /** @type {string | undefined} */
  blockId = undefined;

  /** @type {boolean} */
  isRefreshing = false;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {CustomEvent | undefined} */
  lastUtilityEvent = undefined;

  /**
   * Get the section ID dynamically by finding the parent Shopify section wrapper.
   * Shopify sections have wrappers with id="shopify-section-{section-id}"
   * This ensures we get the correct section ID for the current page, not a cached value.
   * @returns {string|null} The section ID or null if not found
   */
  getSectionId() {
    // First try the data attribute (works for static pages)
    const dataAttrId = this.dataset.sectionId;

    // Find the parent section wrapper by traversing up the DOM
    const sectionWrapper = this.closest('[id^="shopify-section-"]');

    if (sectionWrapper) {
      // Extract section ID from "shopify-section-{section-id}"
      const wrapperId = sectionWrapper.id;
      const sectionId = wrapperId.replace("shopify-section-", "");
      log("Found parent section", { wrapperId, sectionId });
      return sectionId;
    }

    // Fallback to data attribute if no wrapper found
    if (dataAttrId) {
      log("Using data-section-id fallback", { sectionId: dataAttrId });
      return dataAttrId;
    }

    log("No section ID found");
    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Read data attributes in connectedCallback (not constructor)
    // Per Web Components spec, attributes may not be available in constructor
    this.blockId = this.dataset.blockId;

    log("Connected to DOM", {
      sectionId: this.getSectionId(),
      blockId: this.blockId,
    });

    // Setup event listeners with AbortController for cleanup
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    // Note: We intentionally do NOT listen to mysa:utility-partner-loaded
    // Server-side rendering handles initial state from cart.attributes._utility_partner_code
    // Listening to loaded would cause unnecessary refresh on page load
    document.addEventListener(
      MysaEvents.utilityPartnerChanged,
      (e) => this.#onUtilityChanged(/** @type {CustomEvent} */ (e)),
      { signal },
    );
    document.addEventListener(
      MysaEvents.utilityPartnerCleared,
      (e) => this.#onUtilityChanged(/** @type {CustomEvent} */ (e)),
      { signal },
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    log("Disconnected from DOM");
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Handle utility partner change/clear events
   * @param {CustomEvent} event - The utility event
   */
  async #onUtilityChanged(event) {
    log("Utility event received", {
      eventType: event.type,
      detail: event.detail,
    });

    // Store event for fallback use
    this.lastUtilityEvent = event;

    // Debounce multiple rapid events - set flag immediately to prevent
    // race conditions during the delay period
    if (this.isRefreshing) {
      log("Already refreshing, skipping");
      return;
    }
    this.isRefreshing = true;

    // Wait for cart attributes to propagate through Shopify's systems
    // Cart attributes are updated asynchronously, so we need a delay
    // before the Section Rendering API can fetch the updated state
    await this.#delay(250);

    await this.refreshContent();
  }

  /**
   * Delay helper for async operations
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  #delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Refresh the banner content using Section Rendering API
   */
  async refreshContent() {
    // Get section ID dynamically from parent wrapper (not cached data attribute)
    const sectionId = this.getSectionId();

    if (!sectionId) {
      log("No section ID available, cannot refresh");
      this.isRefreshing = false;
      return;
    }

    log("Refreshing content via Section Rendering API", { sectionId });

    try {
      // Build the URL for section rendering
      const url = new URL(window.location.href);
      url.searchParams.set("sections", sectionId);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Section fetch failed: ${response.status}`);
      }

      const data = await response.json();
      const sectionHtml = data[sectionId];

      if (!sectionHtml) {
        log("No section HTML in response");
        return;
      }

      log("Got section HTML, parsing...");

      // Parse the HTML and find our specific block
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, "text/html");

      // Find the new sale-banner element (by block ID if available, otherwise first one)
      let newBanner;
      if (this.blockId) {
        newBanner = doc.querySelector(
          `sale-banner[data-block-id="${this.blockId}"]`,
        );
      }
      if (!newBanner) {
        newBanner = doc.querySelector("sale-banner");
      }

      if (!newBanner) {
        log("Could not find sale-banner in response");
        return;
      }

      log("Found new banner, replacing content");

      // Replace our content with the new content
      this.className = newBanner.className;
      this.innerHTML = newBanner.innerHTML;

      // Update data attributes
      for (const attr of newBanner.attributes) {
        if (attr.name.startsWith("data-")) {
          this.setAttribute(attr.name, attr.value);
        }
      }

      log("Content refreshed successfully");
    } catch (error) {
      console.error("[SaleBanner] Failed to refresh content:", error);
      reportError(error, { tags: { op: "refresh-content" } });
      // Fallback to manual JS update when Section Rendering API fails
      log("Falling back to manual content update");
      await this.manualContentUpdate(this.lastUtilityEvent);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Fallback: Manually update banner content when Section Rendering API fails
   * Gets utility data from the utility providers JSON and updates DOM directly
   * @param {Event} [event] - The utility partner event that triggered the update
   */
  async manualContentUpdate(event) {
    try {
      // Get utility partner data from the JSON script tag
      const utilityData = this.getUtilityPartnerData();

      // Get partner code from event detail or fallback to selection
      let partnerCode = null;
      let partnerName = null;

      if (event?.detail) {
        // Event detail may have partnerCode directly or in selection object
        partnerCode = event.detail.partnerCode || event.detail.code;
        partnerName = event.detail.partnerName || event.detail.name;
        log("Got partner from event", { partnerCode, partnerName });
      }

      // If no code from event, try selection
      if (!partnerCode) {
        const selection = await this.getUtilitySelection();
        if (selection) {
          partnerCode = selection.partnerCode;
          partnerName = selection.partnerName;
          log("Got partner from selection", { partnerCode, partnerName });
        }
      }

      log("Manual update", { partnerCode, partnerName, utilityData });

      const textElement = this.querySelector("[data-banner-text]");
      const logoWrapper = this.querySelector("[data-utility-logo]");

      // Check if this is a "cleared" event
      const isCleared = event?.type === "mysa:utility-partner-cleared";

      if (!isCleared && partnerCode && utilityData?.providers) {
        // Find the matching provider (v2: code and handle both mirror metaobject handle)
        const provider = utilityData.providers.find(
          (p) => p.code === partnerCode || p.handle === partnerCode,
        );

        log("Provider lookup result", { partnerCode, provider });

        if (provider) {
          log("Found provider for manual update", {
            name: provider.name,
            bannerText: provider.bannerText,
            hasLogo: !!provider.logoSvg,
          });

          // Update class to rebate style
          this.classList.remove("as-dark");
          this.classList.add("as-rebate");

          // Update text if banner text available
          if (textElement) {
            if (provider.bannerText) {
              textElement.textContent = provider.bannerText;
              log("Updated text to:", provider.bannerText);
            }
          }

          // Update logo
          if (logoWrapper) {
            if (provider.logoSvg) {
              logoWrapper.innerHTML = provider.logoSvg;
              logoWrapper.removeAttribute("hidden");
              log("Updated logo SVG");
            } else {
              logoWrapper.setAttribute("hidden", "");
              log("No logo SVG, hiding wrapper");
            }
          }

          // Hide icon when utility is active
          const iconElement = this.querySelector(".material-symbols-rounded");
          if (iconElement) {
            iconElement.setAttribute("hidden", "");
          }

          log("Manual content update completed (utility active)");
          return;
        }

        log("Provider not found in utilityData.providers");
      }

      // No utility or provider not found - restore default state
      log("Restoring default state", { isCleared, partnerCode });
      this.classList.remove("as-rebate");

      // Restore icon visibility
      const iconElement = this.querySelector(".material-symbols-rounded");
      if (iconElement) {
        iconElement.removeAttribute("hidden");
      }

      // Hide utility logo
      if (logoWrapper) {
        logoWrapper.setAttribute("hidden", "");
        logoWrapper.innerHTML = "";
      }

      log("Manual content update completed (default state)");
    } catch (error) {
      console.error("[SaleBanner] Manual content update failed:", error);
      reportError(error, { tags: { op: "manual-update" } });
    }
  }

  /**
   * Get utility providers data from the JSON script tag
   * @returns {Object|null} Parsed utility providers data
   */
  getUtilityPartnerData() {
    const scriptTag = document.querySelector("[data-utility-providers]");
    if (!scriptTag) return null;

    try {
      return JSON.parse(scriptTag.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Get current utility selection from the utility manager.
   *
   * Dynamically imports the manager only here (the rare Section-Rendering
   * fallback path) so the banner doesn't pull the manager onto every page.
   * @returns {Promise<import('./utility-partner-manager').UtilitySelection | null>}
   */
  async getUtilitySelection() {
    const { getUtilityPartnerManager } =
      await import("@theme/utility-partner-manager");
    const manager = getUtilityPartnerManager();
    await manager.initialize();
    return manager.getSelection();
  }
}

if (!customElements.get("sale-banner")) {
  customElements.define("sale-banner", SaleBanner);
}
